import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../../lib/api';
import { mergeDeep, cloneDeep, formatMessage } from '../../utils/i18n';
import { STUDENT_STUDY_MATERIALS_COPY } from '../../i18n/materials';
import PreviewContent from './PreviewContent';

const getToken = () => localStorage.getItem('accessToken') || localStorage.getItem('go2cdl_access_token') || '';

function SearchIcon({ className = 'h-5 w-5' }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true" className={className}><circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" /><path d="m16 16 4 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>;
}

function PreviewIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5"><path d="M2.5 12s3.2-5 9.5-5 9.5 5 9.5 5-3.2 5-9.5 5-9.5-5-9.5-5Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /><circle cx="12" cy="12" r="2.5" fill="none" stroke="currentColor" strokeWidth="2" /></svg>;
}

function DownloadIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5"><path d="M12 3v11m0 0 4-4m-4 4-4-4M4 17v3h16v-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

function SortIcon({ className = 'h-5 w-5' }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true" className={className}><path d="M8 4v16m0 0-3-3m3 3 3-3M16 20V4m0 0-3 3m3-3 3 3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

function FileIcon({ mimeType = '' }) {
  const isPdf = mimeType.includes('pdf');
  const isVideo = mimeType.includes('video');
  const isAudio = mimeType.includes('audio');
  const isImage = mimeType.includes('image');
  const label = isPdf ? 'PDF' : isVideo ? 'MP4' : isAudio ? 'MP3' : isImage ? 'IMG' : 'DOC';
  return (
    <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${isPdf ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-700'}`}>
      <div className="relative">
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-8 w-8"><path d="M6 2h8l4 4v16H6z" fill="currentColor" opacity=".18" /><path d="M14 2v5h5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M6 2h8l4 4v16H6z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>
        <span className="absolute inset-x-0 bottom-[4px] text-center text-[7px] font-extrabold leading-none">{label}</span>
      </div>
    </div>
  );
}

const categoryLabel = (category) => {
  if (!category) return 'General';
  const labels = {
    general: 'General',
    'cdl-manual': 'CDL Manual',
    cdlManual: 'CDL Manual',
    'practice-tests': 'Practice Tests',
    practiceTests: 'Practice Tests',
    'training-videos': 'Training Videos',
    trainingVideos: 'Training Videos',
    'safety-guidelines': 'Safety Guidelines',
    safetyGuidelines: 'Safety Guidelines',
    regulations: 'Regulations',
  };
  return labels[category] || String(category).replace(/[-_]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatFileSize = (bytes) => {
  if (!bytes) return '—';
  const index = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${parseFloat((bytes / (1024 ** index)).toFixed(2))} ${['Bytes', 'KB', 'MB', 'GB'][index]}`;
};

const formatDate = (date) => date ? new Date(date).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric', year: 'numeric' }) : '—';

export default function StudentStudyMaterialsModal({ onClose, copy: copyOverrides }) {
  const copy = useMemo(() => mergeDeep(cloneDeep(STUDENT_STUDY_MATERIALS_COPY.default), copyOverrides || {}), [copyOverrides]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewMaterial, setPreviewMaterial] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  const fetchMaterials = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_BASE}/api/study-materials/student`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!response.ok) throw new Error('Failed');
      setMaterials(await response.json());
    } catch (requestError) {
      console.error('Error fetching study materials:', requestError);
      setError(copy.errors?.fetch || STUDENT_STUDY_MATERIALS_COPY.default.errors.fetch);
    } finally {
      setLoading(false);
    }
  }, [copy.errors?.fetch]);

  useEffect(() => { fetchMaterials(); }, [fetchMaterials]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const categories = useMemo(() => ['all', ...Array.from(new Set(materials.map((material) => material.category || 'general')))], [materials]);
  const visibleMaterials = useMemo(() => {
    const query = search.trim().toLowerCase();
    return materials
      .filter((material) => selectedCategory === 'all' || (material.category || 'general') === selectedCategory)
      .filter((material) => !query || [material.title, material.description, material.originalName, material.category].filter(Boolean).join(' ').toLowerCase().includes(query))
      .sort((a, b) => {
        if (sortBy === 'az') return String(a.title || '').localeCompare(String(b.title || ''));
        const first = new Date(a.createdAt || 0).getTime();
        const second = new Date(b.createdAt || 0).getTime();
        return sortBy === 'oldest' ? first - second : second - first;
      });
  }, [materials, search, selectedCategory, sortBy]);

  const closePreview = () => {
    setPreviewMaterial(null);
    setPreviewUrl(null);
    setPreviewError('');
  };

  const openPreview = async (material) => {
    try {
      setPreviewLoading(true);
      setPreviewError('');
      setPreviewMaterial(material);
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/study-materials/${material.id}/download${token ? `?token=${encodeURIComponent(token)}` : ''}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error('Failed');
      setPreviewUrl(URL.createObjectURL(await response.blob()));
    } catch (previewRequestError) {
      console.error('Failed to preview material:', previewRequestError);
      setPreviewError(copy.preview?.error || STUDENT_STUDY_MATERIALS_COPY.default.preview.error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = (material) => {
    try {
      const token = getToken();
      const link = document.createElement('a');
      link.href = `${API_BASE}/api/study-materials/${material.id}/download${token ? `?token=${encodeURIComponent(token)}` : ''}`;
      link.download = material.originalName || material.filename || material.title;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (downloadError) {
      console.error('Error downloading file:', downloadError);
      alert(copy.errors?.downloadFailed || STUDENT_STUDY_MATERIALS_COPY.default.errors.downloadFailed);
    }
  };

  const actionLabels = copy.list?.actions || STUDENT_STUDY_MATERIALS_COPY.default.list.actions;
  const renderActions = (material) => (
    <div className="flex gap-2 sm:justify-end">
      <button type="button" onClick={() => openPreview(material)} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-[#2862bd] px-3.5 py-2.5 text-sm font-semibold text-[#1d55aa] transition hover:bg-blue-50 sm:flex-none" aria-label={`Preview ${material.title}`}>
        <PreviewIcon /> <span>{actionLabels.preview}</span>
      </button>
      <button type="button" onClick={() => handleDownload(material)} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#073d97] px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#082f70] sm:flex-none" aria-label={`Download ${material.title}`}>
        <DownloadIcon /> <span>{actionLabels.download}</span>
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-50">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:h-20 sm:px-7 lg:px-10">
          <img src="/images/logo.jpg" alt="Go2 CDL" className="h-10 w-20 object-contain sm:h-12 sm:w-24" />
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-7 sm:py-10 lg:px-10">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-3xl font-bold tracking-tight text-[#061b45] sm:text-4xl">Study Materials</h1>
          <button type="button" onClick={onClose} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button>
        </div>

        <section className="mt-7 space-y-4" aria-label="Find study materials">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <label className="relative block w-full sm:w-72 lg:w-80">
                <span className="sr-only">Search materials</span>
                <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search materials" className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
              </label>
              <div className="flex max-w-full gap-2 overflow-x-auto pb-1" aria-label="Material categories">
                {categories.map((category) => {
                  const selected = selectedCategory === category;
                  return <button key={category} type="button" onClick={() => setSelectedCategory(category)} className={`whitespace-nowrap rounded-xl border px-4 py-3 text-sm font-semibold transition ${selected ? 'border-[#082c64] bg-[#082c64] text-white shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100'}`}>{category === 'all' ? 'All' : categoryLabel(category)}</button>;
                })}
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 sm:justify-end">
              <span className="text-sm font-medium text-slate-500">{visibleMaterials.length} {visibleMaterials.length === 1 ? 'material' : 'materials'}</span>
              <label className="relative inline-flex items-center text-slate-600">
                <SortIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
                <span className="sr-only">Sort materials</span>
                <select value={sortBy} onChange={(event) => setSortBy(event.target.value)} className="cursor-pointer appearance-none rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-10 text-sm font-semibold shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100">
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="az">Name A–Z</option>
                </select>
                <svg viewBox="0 0 24 24" aria-hidden="true" className="pointer-events-none absolute right-3 h-4 w-4"><path d="m7 10 5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
              </label>
            </div>
          </div>
        </section>

        {error && <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">{error}</div>}

        <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {loading ? (
            <div className="flex min-h-72 items-center justify-center gap-3 text-slate-500"><span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" />{copy.list?.loading || STUDENT_STUDY_MATERIALS_COPY.default.list.loading}</div>
          ) : visibleMaterials.length === 0 ? (
            <div className="flex min-h-72 flex-col items-center justify-center p-8 text-center"><div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-2xl">📚</div><p className="mt-4 text-lg font-bold text-[#061b45]">{search || selectedCategory !== 'all' ? 'No matching materials' : (copy.list?.emptyTitle || STUDENT_STUDY_MATERIALS_COPY.default.list.emptyTitle)}</p></div>
          ) : (
            <>
              <div className="hidden md:block">
                <table className="w-full border-collapse text-left">
                  <thead className="border-b border-slate-200 bg-slate-50/80 text-xs font-bold uppercase tracking-wide text-slate-500"><tr><th className="px-6 py-4">Name</th><th className="px-6 py-4">Details</th><th className="px-6 py-4 text-right">Actions</th></tr></thead>
                  <tbody>
                    {visibleMaterials.map((material) => <tr key={material.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/70"><td className="px-6 py-4"><div className="flex items-center gap-4"><FileIcon mimeType={material.mimeType} /><div className="min-w-0"><p className="truncate font-bold text-[#061b45]">{material.title}</p>{material.description && <p className="mt-0.5 truncate text-sm text-slate-500">{material.description}</p>}</div></div></td><td className="px-6 py-4 text-sm text-slate-500"><span>{categoryLabel(material.category)}</span><span className="mx-2 text-slate-300">•</span><span>{formatFileSize(material.size)}</span><span className="mx-2 text-slate-300">•</span><span>{formatDate(material.createdAt)}</span></td><td className="px-6 py-4">{renderActions(material)}</td></tr>)}
                  </tbody>
                </table>
              </div>
              <div className="divide-y divide-slate-100 md:hidden">
                {visibleMaterials.map((material) => <article key={material.id} className="p-4"><div className="flex items-start gap-3"><FileIcon mimeType={material.mimeType} /><div className="min-w-0 flex-1"><h2 className="break-words font-bold text-[#061b45]">{material.title}</h2><p className="mt-1 text-sm text-slate-500">{categoryLabel(material.category)} <span className="mx-1 text-slate-300">•</span> {formatFileSize(material.size)}</p><p className="mt-1 text-xs text-slate-400">Added {formatDate(material.createdAt)}</p>{material.description && <p className="mt-2 text-sm text-slate-600">{material.description}</p>}</div></div><div className="mt-4">{renderActions(material)}</div></article>)}
              </div>
            </>
          )}
        </section>
      </main>

      {previewMaterial && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-3 sm:p-6">
          <div className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-4 sm:p-5"><div className="min-w-0"><h2 className="truncate text-lg font-bold text-[#061b45]">{formatMessage(copy.preview?.title || STUDENT_STUDY_MATERIALS_COPY.default.preview.title, { title: previewMaterial.title })}</h2><p className="mt-1 truncate text-xs text-slate-500">{previewMaterial.originalName} · {previewMaterial.mimeType}</p></div><button type="button" onClick={closePreview} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xl text-slate-500 hover:bg-slate-100 hover:text-slate-700" aria-label={copy.preview?.closeAria || STUDENT_STUDY_MATERIALS_COPY.default.preview.closeAria}>×</button></div>
            <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 p-3 sm:p-5">
              {previewLoading && <div className="flex h-72 items-center justify-center text-slate-500">{copy.preview?.loading || STUDENT_STUDY_MATERIALS_COPY.default.preview.loading}</div>}
              {previewError && !previewLoading && <div className="p-6 text-center text-sm text-red-600">{previewError}</div>}
              {!previewLoading && previewUrl && <PreviewContent url={previewUrl} mime={previewMaterial.mimeType} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
